/**
 * Channel List Sidebar — multicolor channel pills (Talk2 style)
 * Admin assigns channels; users auto-join on connect.
 */
import React from 'react';
import { Hash, Radio, Volume2, VolumeX, Star } from 'lucide-react';
import useStore from '../store/useStore';
import { getSocket } from '../hooks/useSocket';
import MiniWave from './MiniWave';

// Map channel name → talk2 multicolor hue
const NAME_TO_HUE = {
  'general':       { hue: '#007AFF', tint: '#E6F1FF', glow: 'rgba(0,122,255,.35)' },
  'operaciones':   { hue: '#007AFF', tint: '#E6F1FF', glow: 'rgba(0,122,255,.35)' },
  'operations':    { hue: '#007AFF', tint: '#E6F1FF', glow: 'rgba(0,122,255,.35)' },
  'soporte':       { hue: '#FF9F0A', tint: '#FFF3DE', glow: 'rgba(255,159,10,.35)' },
  'support':       { hue: '#FF9F0A', tint: '#FFF3DE', glow: 'rgba(255,159,10,.35)' },
  'logística':     { hue: '#FF9F0A', tint: '#FFF3DE', glow: 'rgba(255,159,10,.35)' },
  'logistics':     { hue: '#FF9F0A', tint: '#FFF3DE', glow: 'rgba(255,159,10,.35)' },
  'seguridad':     { hue: '#FF375F', tint: '#FFEAEF', glow: 'rgba(255,55,95,.35)' },
  'security':      { hue: '#FF375F', tint: '#FFEAEF', glow: 'rgba(255,55,95,.35)' },
  'emergencias':   { hue: '#FF2D55', tint: '#FFE5EC', glow: 'rgba(255,45,85,.45)' },
  'emergencia':    { hue: '#FF2D55', tint: '#FFE5EC', glow: 'rgba(255,45,85,.45)' },
  'emergency':     { hue: '#FF2D55', tint: '#FFE5EC', glow: 'rgba(255,45,85,.45)' },
  'campo':         { hue: '#34C759', tint: '#E5F9EC', glow: 'rgba(52,199,89,.35)' },
  'field':         { hue: '#34C759', tint: '#E5F9EC', glow: 'rgba(52,199,89,.35)' },
  'central':       { hue: '#AF52DE', tint: '#F4E9FB', glow: 'rgba(175,82,222,.35)' },
  'dispatch':      { hue: '#AF52DE', tint: '#F4E9FB', glow: 'rgba(175,82,222,.35)' },
};

// Stable fallback palette by hashed name
const FALLBACK_HUES = [
  { hue: '#007AFF', tint: '#E6F1FF', glow: 'rgba(0,122,255,.35)' },
  { hue: '#AF52DE', tint: '#F4E9FB', glow: 'rgba(175,82,222,.35)' },
  { hue: '#34C759', tint: '#E5F9EC', glow: 'rgba(52,199,89,.35)' },
  { hue: '#FF9F0A', tint: '#FFF3DE', glow: 'rgba(255,159,10,.35)' },
  { hue: '#FF375F', tint: '#FFEAEF', glow: 'rgba(255,55,95,.35)' },
  { hue: '#5856D6', tint: '#ECECFB', glow: 'rgba(88,86,214,.35)' },
  { hue: '#30B0C7', tint: '#E0F4F8', glow: 'rgba(48,176,199,.35)' },
];

function channelStyle(name = '') {
  const k = String(name).trim().toLowerCase();
  if (NAME_TO_HUE[k]) return NAME_TO_HUE[k];
  let h = 0;
  for (let i = 0; i < k.length; i++) h = (h * 31 + k.charCodeAt(i)) >>> 0;
  return FALLBACK_HUES[h % FALLBACK_HUES.length];
}

export default function ChannelList({ onChannelSelect }) {
  const channels = useStore(s => s.channels);
  const assignedChannels = useStore(s => s.assignedChannels);
  const activeChannel = useStore(s => s.activeChannel);
  const mutedChannels = useStore(s => s.mutedChannels);
  const onlineCounts = useStore(s => s.onlineCounts);
  const channelUsers = useStore(s => s.channelUsers);
  const activeTransmittingByChannel = useStore(s => s.activeTransmittingByChannel);
  const user = useStore(s => s.user);
  const toggleMuteChannel = useStore(s => s.toggleMuteChannel);
  const setActiveChannel = useStore(s => s.setActiveChannel);

  const isAdmin = user?.role === 'admin';
  const visibleChannels = isAdmin ? channels : assignedChannels;

  const handleChannelClick = (channel) => {
    setActiveChannel(channel);
    const socket = getSocket();
    if (socket?.connected) {
      socket.emit('channel:setActive', { channelId: channel.id });
    }
    onChannelSelect?.();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 flex items-center justify-between flex-shrink-0">
        <span className="text-[10px] font-bold uppercase tracking-widest text-n-500">Canales</span>
      </div>

      {/* Channel list */}
      <div className="flex-1 overflow-y-auto scrollbar-thin px-3 pb-4 space-y-1">
        {visibleChannels.length === 0 ? (
          <div className="text-center py-10 px-4 flex flex-col items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-n-100 flex items-center justify-center mb-1">
              <Radio size={18} className="text-n-400" aria-hidden="true" />
            </div>
            <p className="text-sm font-semibold text-n-700">
              {isAdmin ? 'Sin canales' : 'Sin canales asignados'}
            </p>
            <p className="text-xs text-n-600 max-w-[220px] leading-snug">
              {isAdmin
                ? 'Crea tu primer canal desde el panel de administración.'
                : 'Pide a un admin que te asigne acceso a canales.'}
            </p>
          </div>
        ) : (
          visibleChannels.map(channel => {
            const isActive = activeChannel?.id === channel.id;
            const count = onlineCounts[channel.id] || 0;
            const c = channelStyle(channel.name);
            const isMuted = mutedChannels.has(channel.id);
            const isPrimary = channel.isPrimary;
            const txUsers = activeTransmittingByChannel[channel.id] || [];
            const txCount = txUsers.length;
            const txList = (channelUsers[channel.id] || []).filter(u => txUsers.includes(u.userId));

            return (
              <div
                key={channel.id}
                className={`group w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200`}
                style={
                  isActive
                    ? { background: c.tint, boxShadow: `0 4px 12px ${c.glow}` }
                    : { background: 'transparent' }
                }
              >
                {/* Hue dot */}
                <button
                  onClick={() => handleChannelClick(channel)}
                  className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 transition-all"
                  style={
                    isActive
                      ? { background: c.hue, color: '#fff', boxShadow: `0 6px 16px ${c.glow}` }
                      : { background: c.tint, color: c.hue }
                  }
                >
                  <Hash size={14} />
                </button>

                {/* Channel info */}
                <button
                  onClick={() => handleChannelClick(channel)}
                  className="flex-1 min-w-0 text-left"
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`text-sm font-semibold truncate tracking-apple-tight ${
                        isActive ? 'text-n-800' : 'text-n-700 group-hover:text-n-800'
                      }`}
                    >
                      {channel.name}
                    </span>
                    {isPrimary && (
                      <Star size={10} style={{ color: '#FFCC00' }} className="flex-shrink-0" fill="currentColor" />
                    )}
                    {txCount > 0 && (
                      <MiniWave color={c.hue} size={12} />
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    {txCount > 0 ? (
                      <>
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: c.hue, boxShadow: `0 0 6px ${c.glow}` }} />
                        <span className="text-[11px] font-semibold" style={{ color: c.hue }}>
                          ● En vivo · {txCount} {txCount === 1 ? 'hablando' : 'hablando'}
                        </span>
                      </>
                    ) : count > 0 ? (
                      <>
                        <div className="w-1.5 h-1.5 rounded-full bg-s-green" />
                        <span className="text-[11px] text-n-500">{count} {count === 1 ? 'miembro' : 'miembros'} · idle</span>
                      </>
                    ) : (
                      <>
                        <div className="w-1.5 h-1.5 rounded-full bg-n-300" />
                        <span className="text-[11px] text-n-400">sin actividad</span>
                      </>
                    )}
                  </div>
                </button>

                {/* Mute toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleMuteChannel(channel.id); }}
                  className={`p-1.5 rounded-md transition-all flex-shrink-0 ${
                    isMuted
                      ? 'text-s-red bg-s-red/10 hover:bg-s-red/20'
                      : 'text-n-400 hover:text-n-700 hover:bg-black/5'
                  }`}
                  title={isMuted ? 'Activar audio' : 'Silenciar canal'}
                >
                  {isMuted ? <VolumeX size={13} /> : <Volume2 size={13} />}
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* User info card */}
      {user && (
        <div
          className="p-3 mx-3 mb-3 rounded-md flex items-center gap-2.5"
          style={{ background: 'rgba(255,255,255,0.65)', border: '1px solid rgba(0,0,0,0.05)' }}
        >
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0 shadow-apple-sm"
            style={{
              background: `linear-gradient(135deg, ${user.avatarColor || '#007AFF'}, #AF52DE)`,
            }}
          >
            {(user.username?.[0] || '?').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-n-800 truncate tracking-apple-tight">
              {user.username}
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full bg-s-green" />
              <span className="text-[11px] text-n-500">{user.role}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
